import { resolveFeatureExplore } from "constants/whats-new.constants";
import { useWhatsNew } from "hooks/use-whats-new";
import type { WhatsNewCtaTarget } from "models/whats-new";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WhatsNewDialog } from "./WhatsNewDialog";

/** Click an element by id once it is in the DOM, retrying across a few frames
 *  so a target that only mounts after navigation (e.g. a lazy landing page)
 *  still receives the click. Gives up silently after `attempts` frames. */
const clickElementWhenReady = (id: string, attempts = 20) => {
  const tick = (remaining: number) => {
    const el = document.getElementById(id);
    if (el) {
      el.click();
      return;
    }
    if (remaining > 0) {
      requestAnimationFrame(() => tick(remaining - 1));
    }
  };
  tick(attempts);
};

/**
 * Wires the What's New release gate ({@link useWhatsNew}) to navigation and
 * mounts the popup. Mount inside the authenticated layout so account-type and
 * access-level context is available.
 */
export const WhatsNewController = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { release, isOpen, markSeen } = useWhatsNew();

  if (!release) return null;

  const handleExplore = (target: WhatsNewCtaTarget) => {
    markSeen();
    if (target.kind === "url") {
      window.open(target.href, "_blank", "noopener,noreferrer");
      return;
    }
    const { route, clickElementId } = resolveFeatureExplore(target.tag);
    navigate(route);
    if (clickElementId) {
      clickElementWhenReady(clickElementId);
    }
  };

  return (
    <WhatsNewDialog
      open={isOpen}
      steps={release.steps}
      title={t("whatsNew.title")}
      onClose={markSeen}
      onExplore={handleExplore}
    />
  );
};
